import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";

import api from "../../services/api";
import { usePagedList } from "../../hook/usePagedList";
import Pager from "../../components/ui/Pager";
import Modal from "../../components/ui/Modal";
import { notify } from "../../components/ui/Toast";
import { FormError, ModalFoot } from "../../components/ui/panelKit";
import { readApiError } from "../../utils/apiError";
import { relativeTime, exactTime } from "../../utils/time";
import { useSearchInput } from "../../hook/useSearchInput";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

/** Mirrors SLUG_RE on the server. Checked here too so a typo is caught before
    a round trip, never instead of the server's own check. */
const SLUG_RE = /^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/;

/**
 * A first guess at the slug, from the name.
 *
 * Only a guess: it stops following the name the moment somebody edits the slug
 * themselves, because the slug is permanent and the name is not -- having the
 * name quietly rewrite a slug the person had already settled on would be the
 * one edit this form must never make on its own.
 */
const slugify = (name) =>
  name
    .toLowerCase()
    .replace(/[ʻʼ'']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

/**
 * What still stops a client from working, as short labels for the register.
 * The same three checks as the warning strip on the detail page; a ticket
 * client adds its own members, so no member is not a problem there.
 */
const setupProblems = (c) =>
  [
    !c.origin_count && "manba yoʻq",
    !c.credential_count && "kalit yoʻq",
    !c.member_count && c.auth_mode !== "ticket" && "aʼzo yoʻq",
  ].filter(Boolean);

/**
 * The register of client systems.
 *
 * A client that cannot work yet is called out in the Sozlanishi cell rather
 * than left as a zero to notice: no allowed origin means the browser refuses
 * to render its widget anywhere, no live key means the client system cannot
 * call the API, and no member means nobody can read its discussions. All three
 * are quiet failures that a customer reports as "the chat is broken". The
 * whole row opens the client.
 *
 * Filtered and paged on the server, like the other registers. This one is
 * small -- a handful of integrating systems -- but its row counts are not:
 * each row carries how many threads the client owns, and that number is drawn
 * from the whole discussion register.
 */
const ClientsPage = () => {
  const { input, setInput, search, setSearch } = useSearchInput();
  const [status, setStatus] = useState("");
  const [perPage, setPerPage] = useState(10);

  // `search` follows the box once typing pauses (useSearchInput): `q` is an
  // ILIKE over slug and name with no trigram index, so not every keystroke.
  const params = useMemo(
    () => ({ ...(search ? { q: search } : {}), ...(status ? { status } : {}) }),
    [search, status],
  );

  const { rows, count, loading, error, page, setPage, totalPages } = usePagedList(
    "/clients/",
    params,
    perPage,
  );

  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const openForm = () => {
    setForm({ name: "", slug: "" });
    setSlugTouched(false);
    setFormError("");
  };

  const create = async () => {
    const slug = form.slug.trim();
    if (!form.name.trim()) {
      setFormError("Nomini kiriting.");
      return;
    }
    if (!SLUG_RE.test(slug)) {
      setFormError(
        "Slug faqat kichik lotin harflari, raqamlar va defisdan iborat boʻladi; " +
          "harf yoki raqam bilan boshlanib tugashi kerak. Masalan: e-kengash",
      );
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const res = await api.post("/clients/", { slug, name: form.name.trim() });
      setForm(null);
      notify.success("Mijoz tizimi yaratildi.");
      // Straight to the client rather than back to the list: a new client has
      // no allowed origin, no member and no credential, so it cannot work yet
      // -- it is born in exactly the state the register flags as broken. The
      // detail page is where all three are fixed.
      navigate(`/mijoz-tizimlari/${res.data.id}`);
    } catch (e) {
      // The API serves both panels and answers in English; this one is Uzbek
      // throughout, so the single error worth expecting here is worded rather
      // than relayed. Anything else still falls through to the server's text,
      // which beats inventing a message for a case we did not predict.
      const taken = e?.response?.status === 400 && e?.response?.data?.slug;
      setFormError(
        taken
          ? "Bu slug allaqachon band — boshqasini tanlang."
          : readApiError(e, { slug: "Slug", name: "Nomi" },
                         "Mijoz tizimini yaratib boʻlmadi."),
      );
    } finally {
      setSaving(false);
    }
  };

  const filtering = Boolean(search || status);
  const from = count === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, count);

  return (
    <div className="ms-panel">
      <form
        className="ms-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(input.trim());
        }}
      >
        <div className="ms-search">
          <Icon icon="tabler:search" />
          <input
            type="text"
            placeholder="Slug yoki nomi..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Qidiruv"
          />
        </div>

        <select
          className="ms-select ms-select--sm"
          value={status}
          aria-label="Holati"
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">Barcha holatlar</option>
          <option value="active">Faol</option>
          <option value="off">Oʻchirilgan</option>
        </select>

        {filtering && (
          <button
            type="button"
            className="ms-btn ms-btn--ghost"
            onClick={() => {
              setInput("");
              setSearch("");
              setStatus("");
            }}
          >
            <Icon icon="tabler:x" />
            Tozalash
          </button>
        )}

        <div className="ms-toolbar-spacer" />

        <button type="button" className="ms-btn ms-btn--primary" onClick={openForm}>
          <Icon icon="tabler:plus" />
          Yangi mijoz tizimi
        </button>
      </form>

      {loading ? (
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      ) : error ? (
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{error}</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="ms-state">
          <Icon icon="tabler:server-off" />
          <span>{filtering ? "Hech narsa topilmadi" : "Mijoz tizimlari yoʻq"}</span>
        </div>
      ) : (
        <>
          <div className="ms-table-wrap">
            <table className="ms-table ms-table--compact">
              <thead>
                <tr>
                  <th>Mijoz tizimi</th>
                  <th style={{ width: 120 }}>Holati</th>
                  <th style={{ width: 120 }}>Kirish usuli</th>
                  <th style={{ width: 290 }}>Sozlanishi</th>
                  <th style={{ width: 90 }}>Aʼzolar</th>
                  <th style={{ width: 110 }}>Muhokamalar</th>
                  <th style={{ width: 140 }}>Oxirgi faollik</th>
                  <th style={{ width: 44 }} aria-label="Ochish" />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const problems = setupProblems(c);
                  const open = () => navigate(`/mijoz-tizimlari/${c.id}`);
                  return (
                    <tr
                      key={c.id}
                      className="is-clickable"
                      tabIndex={0}
                      onClick={open}
                      onKeyDown={(e) => e.key === "Enter" && open()}
                    >
                      <td>
                        <div className="ms-namecell">
                          {/* A real link too, so middle-click and copy-link work. */}
                          <Link
                            to={`/mijoz-tizimlari/${c.id}`}
                            className="ms-sublink"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <b>{c.name}</b>
                          </Link>
                          <span>{c.slug}</span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`ms-badge ms-badge--${c.is_active ? "success" : "danger"}`}
                          title={c.is_active ? "" : exactTime(c.deactivated_at)}
                        >
                          {c.is_active ? "Faol" : "Oʻchirilgan"}
                        </span>
                      </td>
                      <td>
                        <span className="ms-badge ms-badge--neutral">
                          {c.auth_mode === "ticket" ? "Ticket" : "QR"}
                        </span>
                      </td>
                      <td>
                        {!c.is_active ? (
                          // Switched off on purpose: its setup gaps are not news.
                          <span className="ms-td-muted">—</span>
                        ) : problems.length === 0 ? (
                          <span className="ms-badge ms-badge--success">tayyor</span>
                        ) : (
                          <span style={{ display: "inline-flex", gap: 4 }}>
                            {problems.map((p) => (
                              <span key={p} className="ms-badge ms-badge--progress">
                                {p}
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className="ms-td-num">{c.member_count || 0}</td>
                      <td className="ms-td-num">{c.thread_count || 0}</td>
                      <td className="ms-td-muted" title={exactTime(c.last_activity_at)}>
                        {c.last_activity_at ? relativeTime(c.last_activity_at) : "—"}
                      </td>
                      <td>
                        <span className="ms-rowgo">
                          <Icon icon="tabler:chevron-right" />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="ms-tablefoot">
            <span className="ms-tablefoot-count">
              Koʻrsatilmoqda <b>{from}</b>—<b>{to}</b> / jami <b>{count}</b> ta
            </span>

            <div className="ms-tablefoot-right">
              <label className="ms-perpage">
                <span>Koʻrsatish</span>
                <select
                  className="ms-select ms-select--sm"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  {PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <Pager page={page} totalPages={totalPages} onPage={setPage} />
            </div>
          </div>
        </>
      )}

      <Modal
        open={Boolean(form)}
        title="Yangi mijoz tizimi"
        onClose={() => setForm(null)}
        footer={
          <ModalFoot onCancel={() => setForm(null)} onSave={create} saving={saving}
                     saveLabel="Yaratish" />
        }
      >
        {form && (
          <>
            <FormError>{formError}</FormError>

            <div className="ms-field">
              <label htmlFor="c-name">Nomi</label>
              <input
                id="c-name"
                autoFocus
                placeholder="e-Kengash"
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setForm((f) => ({
                    ...f,
                    name,
                    slug: slugTouched ? f.slug : slugify(name),
                  }));
                }}
              />
              <span className="ms-field-hint">
                Panelda va kabinetda koʻrinadigan nom. Keyin oʻzgartirish mumkin.
              </span>
            </div>

            <div className="ms-field">
              <label htmlFor="c-slug">Slug</label>
              <input
                id="c-slug"
                className="ms-mono"
                autoComplete="off"
                placeholder="e-kengash"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }));
                }}
              />
              {/* The one field on this form that cannot be taken back. */}
              <span className="ms-field-hint">
                <b>Keyinchalik oʻzgartirib boʻlmaydi.</b> Slug vidjetni joylashtirgan
                har bir sahifaga yoziladi, shuning uchun uni oʻzgartirish oʻsha
                sahifalarni sezdirmay ishdan chiqaradi. Faqat kichik lotin harflari,
                raqamlar va defis.
              </span>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ClientsPage;
